import React, {useContext} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import MetaInfoBar from '../components/MetaInfoBar.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {AuthContext} from '../../../context/auth';
import {getPlanStage} from '../../../utils/productAccess';
import {TIER_COLOR, BAND_COLOR} from '../constants';
import {findCard, kpiText, kpiNote, rowsFrom, movementDelta} from '../../DashboardCommon/dashboardCards';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';

/* The 7 canonical learning-program topics (T1-T7). "Youth AI-Digital
   Activities" is T8 and is deliberately excluded — see the mockup's own
   note: "Of 7 programs (T1-T7, not T8) …". */
const PROGRAM_TOPICS = [
    'Self-regulated AI-Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Critical Information Management',
    'Authentic and Ethical Digital Identity',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
];

/* A value the data doesn't carry shows as "—". This page used to fill gaps
   with realistic sample values (a school name, 60 / 56% / 82%, fixed dates),
   which sat next to real numbers and could not be told apart from them. */
const NO_VALUE = '—';

/* Learning Impact (Data Map CD-03-06 … 09): CD.S_8.C_2 Programs Deployed and
   C_5 Avg Impact Rate. The mockup's "Programs Created" tile was renamed to
   "Programs Deployed" by the designer (its second tile is a duplicate), and
   "Priority Curriculum Coverage" was removed from the datahouse by D-10 — so
   two tiles, both live.
   COS Movement (CD-03-10 … 13): CD.S_9.C_5 reassessment status, C_2 / C_3 / C_4
   MovementCards — each shows its delta, or "Pending" until the class reassesses. */
const learningImpactKpis = (card) => [
    {label: 'Programs Deployed', value: kpiText(card('C_2')), sub: ''},
    {label: 'Avg Impact Rate', value: kpiText(card('C_5'), true), sub: 'Learning signal'},
];
const cosMovementKpis = (card) => {
    const pending = (sub) => (c) => {
        const d = movementDelta(c);
        return {value: d, sub: d === 'Pending' ? sub : ''};
    };
    return [
        {label: 'Reassessment', value: kpiText(card('C_5')), sub: 'next COS cycle'},
        {label: 'Composite Move', ...pending('awaiting reassess')(card('C_2'))},
        {label: 'Risk Reduction', ...pending('awaiting reassess')(card('C_3'))},
        {label: 'Context Move', ...pending('awaiting reassess')(card('C_4'))},
    ];
};

const FOOTER_NOTE = 'Movement confirms only after reassessment — learning signal above is intervention evidence, not confirmed improvement.';
const PROGRAMS_FOOTER = 'Recommended system interventions for your class is listed in your school’s dashboard. Contact your school administrator to view the list.';

const getBandColor = (band) => BAND_COLOR[band] || TIER_COLOR[band] || '#A5A5A5';

const KpiCard = ({label, value, sub, tier}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
        display: 'flex', flexDirection: 'column',
    }}>
        <Box sx={{backgroundColor: tier ? getBandColor(tier) : ORANGE, height: '6px', flexShrink: 0}}/>
        <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', flex: 1}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: {xs: '28px', md: '32px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {tier ? (
                <Box sx={{mt: 1.2, display: 'inline-block', backgroundColor: getBandColor(tier), color: '#fff', fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px'}}>
                    {tier}
                </Box>
            ) : sub ? (
                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.8}}>{sub}</Typography>
            ) : null}
        </Box>
    </Box>
);

/* Same uppercase small-caps label already used for a group header within
   this same ClassDashboard (Readiness.page.jsx's indicator label, and the
   KPI-card labels on this very page) — one established color (ORANGE) for
   every section header here, not a different invented color per section. */
const SectionHeader = ({text}) => (
    <Typography sx={{fontSize: '13px', fontWeight: 700, color: ORANGE, letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1.5, mt: 3}}>
        {text}
    </Typography>
);

/* Same rubric band chip colors already defined in constants.js (BAND_COLOR/
   TIER_COLOR) and used everywhere else a band shows up — these are class
   scored "High" per the same rubric, so the chip reuses that "High" color
   rather than a separate one-off.
   One-card-per-program doesn't scale — up to all 7 T1-T7 topics can qualify,
   and 7 separate bordered cards turns into visual clutter. A single list
   card (rows separated by hairlines, like Strengths/Needs Attention
   elsewhere in this app) stays compact and clean at any count. */
const ProgramRow = ({name, band = 'High'}) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
        py: 1.2, px: 2.5, borderBottom: '1px solid #F3F4F6', '&:last-child': {borderBottom: 'none'},
    }}>
        <Typography sx={{fontSize: '13.5px', color: '#374151'}}>{name}</Typography>
        <Box sx={{backgroundColor: getBandColor(band), color: '#fff', fontSize: '11px', fontWeight: 700, px: 1.4, py: 0.4, borderRadius: '20px', whiteSpace: 'nowrap'}}>
            {band}
        </Box>
    </Box>
);

const OVERVIEW_SECTION_CODE = 'S_2';
const LEARNING_DEPLOYMENT_SECTION_CODE = 'S_7';
const PRIORITY_ACTIONS_SECTION_CODE = 'S_8';
const REASSESSMENT_SECTION_CODE = 'S_9';

const CARD_SECTIONS = [
    OVERVIEW_SECTION_CODE,
    LEARNING_DEPLOYMENT_SECTION_CODE,
    PRIORITY_ACTIONS_SECTION_CODE,
    REASSESSMENT_SECTION_CODE,
];

const ExecutiveOverviewSection = () => {
    const {authUser} = useContext(AuthContext);
    const stage = getPlanStage(authUser);
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    // Defensively locate section S_2 — same source Summary.page.jsx reads.
    let sectionData = null;
    if (dashboardData.section_code === OVERVIEW_SECTION_CODE) {
        sectionData = dashboardData;
    } else if (dashboardData.cards?.sections) {
        sectionData = dashboardData.cards.sections.find(s => s.section_code === OVERVIEW_SECTION_CODE);
    } else if (Array.isArray(dashboardData.sections)) {
        sectionData = dashboardData.sections.find(s => s.section_code === OVERVIEW_SECTION_CODE);
    } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
        sectionData = dashboardData;
    }

    if (!sectionData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const cards = sectionData?.cards || [];

    // This mockup's meta bar (SCHOOL / Class / BASELINE / LEARNING / REASSESS)
    // is a different field set than the existing Executive Summary page's
    // ORGCard columns (CLASS / GRADE / SCHOOL / ASSESSMENT WINDOW / REPORT
    // DATE) — pulling table_objects wholesale would show the wrong fields
    // whenever S_2 has real data. SCHOOL and CLASS come across as-is, and the
    // baseline is the assessment window. LEARNING is the program window the
    // program cell carries (Data Map CD-02-01: S_7 C_2 kpi_data start_date /
    // end_date) — "—" when no program is assigned to the class yet.
    // REASSESS is the S_9 C_5 status.
    const orgCard = cards.find(c => c.card_type === 'ORGCard') || {};
    const findOrgValue = (columnName) => orgCard.table_objects?.find(o => o.column_name === columnName)?.values?.[0];
    const s8 = (c) => findCard(dashboardData, PRIORITY_ACTIONS_SECTION_CODE, c);
    const s9 = (c) => findCard(dashboardData, REASSESSMENT_SECTION_CODE, c);
    const reassessStatus = s9('C_5')?.kpi_value;
    const programWindow = findCard(dashboardData, LEARNING_DEPLOYMENT_SECTION_CODE, 'C_2')?.kpi_data || s8('C_2')?.kpi_data || {};
    const learningWindow = programWindow.start_date
        ? `${programWindow.start_date} ~ ${programWindow.end_date || programWindow.start_date}`
        : NO_VALUE;
    const metaItems = [
        {label: 'SCHOOL', value: findOrgValue('SCHOOL') || NO_VALUE},
        {label: 'CLASS', value: findOrgValue('CLASS') || NO_VALUE},
        {label: 'BASELINE', value: findOrgValue('ASSESSMENT WINDOW') || NO_VALUE},
        {label: 'LEARNING', value: learningWindow},
        {label: 'REASSESS', value: reassessStatus ? String(reassessStatus) : 'Pending'},
    ];

    const kpiCards = cards.filter(c => c.card_type === 'KPICard') || [];
    const findKpi = (needle) => kpiCards.find(k => k.kpi_name?.toUpperCase().includes(needle));

    const compositeCard = findKpi('COMPOSITE') || {};
    const riskCard = findKpi('RISK') || {};
    const participationCard = findKpi('PARTICIPATION') || {};
    const contextCard = findKpi('CONTEXT') || {};

    const baselineKpis = [
        // A KPI under the n floor (suppressed) reads "—" with the reason underneath and no band.
        {
            label: 'Readiness Composite Score',
            value: compositeCard.kpi_value != null ? Math.trunc(compositeCard.kpi_value) : NO_VALUE,
            tier: compositeCard.kpi_band,
            sub: kpiNote(compositeCard),
        },
        {
            label: 'Cyber Risk Experience',
            value: riskCard.kpi_value != null ? `${Math.trunc(riskCard.kpi_value)}%` : NO_VALUE,
            tier: riskCard.kpi_band,
            sub: kpiNote(riskCard),
        },
        {
            label: 'Participation',
            value: participationCard.kpi_value != null ? `${Math.trunc(participationCard.kpi_value)}%` : NO_VALUE,
            sub: participationCard.kpi_data ? `${participationCard.kpi_data.participants} / ${participationCard.kpi_data.total}` : '',
        },
        {
            label: 'Context & Support',
            value: contextCard.kpi_value != null ? Math.trunc(contextCard.kpi_value) : NO_VALUE,
            tier: contextCard.kpi_band,
            sub: kpiNote(contextCard),
        },
    ];

    // Recommended Learning Programs: of the 7 T1-T7 topics (T8 "Youth
    // AI-Digital Activities" excluded), list the ones this class scored
    // "High" on — pulled from the same S_2 area-score chart Summary.page.jsx
    // uses, so it tracks the class's real scores rather than a fixed list.
    const chartCard = cards.find(c => c.card_type === 'BarChartBMCard') || {};
    const scoredHighTopics = (chartCard.items || [])
        .filter(item => PROGRAM_TOPICS.some(name => name.toLowerCase() === (item.label || '').trim().toLowerCase()))
        .filter(item => item.band === 'High')
        .map(item => item.label);
    // The datahouse sends this list (CD.S_8.C_6, PriorityTableCard kind LP,
    // mode class): one row per program, the class's band for its area in
    // row_meta. The band filter above is the fallback when the card is missing.
    // No sample fallback: an empty list says so instead of naming programs
    // the class's data never pointed to.
    const lpCard = s8('C_6');
    const recommendedPrograms = lpCard
        ? rowsFrom(lpCard, {name: ['Recommended Learning', 'Recommended Learning Program', 'Learning Program', 'Program']})
            .map((r, i) => ({name: r.name, band: lpCard.row_meta?.[i]?.band || 'High'}))
            .filter((r) => r.name && r.name !== '—')
        : scoredHighTopics.map((name) => ({name, band: 'High'}));

    const showBaseline = stage !== 'LC';
    const showRecommendedPrograms = stage !== 'LC';
    const showLearningImpactAndCos = stage !== 'P0';

    return (
        <ClassDashboardShell>
            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.3}}>
                    Class Executive Overview
                </Typography>
                <Typography sx={{fontSize: '14px', color: '#4B5563', fontStyle: 'italic', mb: 0.8}}>
                    One-page command center — administration, baseline, Learning Improvement, and COS movement at a glance.
                </Typography>
            </Box>

            <MetaInfoBar items={metaItems}/>

            {/* Section visibility follows the org's real plan stage
                (authUser.dq_pro_plan.plan_key, resolved above), not a manual
                toggle:
                P0: Baseline + Recommended Learning Programs.
                P1: Baseline + Recommended Learning Programs + Learning
                Impact + COS Movement + footer note.
                LC: Baseline and Recommended Learning Programs skipped;
                Learning Impact shown; COS Movement skipped entirely. */}

            {showBaseline && (
                <>
                    <SectionHeader text="Baseline"/>
                    <Grid container spacing={2.5}>
                        {baselineKpis.map((k) => (
                            <Grid item xs={12} sm={6} md={3} key={k.label}>
                                <KpiCard {...k}/>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {showRecommendedPrograms && (
                <Box sx={{mt: 3, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden'}}>
                    <Box sx={{backgroundColor: getBandColor('High'), height: '4px'}}/>
                    <Box sx={{p: 3, pb: 1.5}}>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                            Recommended Learning Programs
                        </Typography>
                    </Box>
                    <Box sx={{mb: 1}}>
                        {recommendedPrograms.length > 0
                            ? recommendedPrograms.map((p, i) => <ProgramRow key={`${p.name}-${i}`} name={p.name} band={p.band}/>)
                            : (
                                <Typography sx={{fontSize: '13.5px', color: '#6A7282', px: 2.5, py: 1.2}}>
                                    No program to recommend yet — this appears once the class's area scores are in.
                                </Typography>
                            )}
                    </Box>
                    <Typography sx={{fontSize: '12.5px', color: '#6A7282', lineHeight: 1.6, px: 3, pb: 3}}>
                        {PROGRAMS_FOOTER}
                    </Typography>
                </Box>
            )}

            {showLearningImpactAndCos && (
                <>
                    <SectionHeader text="Learning Impact"/>
                    <Grid container spacing={2.5}>
                        {learningImpactKpis(s8).map((k) => (
                            <Grid item xs={12} sm={6} key={k.label}>
                                <KpiCard {...k}/>
                            </Grid>
                        ))}
                    </Grid>

                    {stage !== 'LC' && (
                        <>
                            <SectionHeader text="COS Movement"/>
                            <Grid container spacing={2.5} sx={{mb: 1}}>
                                {cosMovementKpis(s9).map((k) => (
                                    <Grid item xs={12} sm={6} md={3} key={k.label}>
                                        <KpiCard {...k}/>
                                    </Grid>
                                ))}
                            </Grid>
                            <Typography sx={{fontSize: '12px', color: '#6A7282', fontStyle: 'italic', mt: 1.5}}>
                                {FOOTER_NOTE}
                            </Typography>
                        </>
                    )}
                </>
            )}
        </ClassDashboardShell>
    );
};

export default ExecutiveOverviewSection;
